/**
 * lib/clockingRules.ts
 * ------------------------------------------------------------------
 * HR DOMAIN — what a day's punches MEAN: in at, out at, hours worked,
 * and whether (and by how much) somebody was late.
 *
 * WORKED OUT ON SIGHT, NOT STORED. Until Oct 2026 "late" and "half day"
 * were decided once, at upload, from two boxes on the upload screen, and
 * frozen into each hrAttendance record. An August–September upload run
 * with "half day under 10 hours" labelled 3,500 ordinary days half days,
 * and a screen then showed "Late · 12.05 h" — the hours worked, sitting
 * beside a late badge, read as "late by 12 hours". Every screen now
 * derives the day from its punches and the school's rules
 * (SCHOOL.clocking), so changing a rule corrects every day already
 * uploaded and nothing has to be re-uploaded or rewritten.
 *
 * The school's rules (Oct 2026):
 *   - late = first punch after the day's start, by the minutes past it;
 *   - leaving late is nobody's problem and is not flagged;
 *   - there are no half days;
 *   - a day with an odd number of punches is missing a clock-out (or a
 *     clock-in): its hours are unknown, not zero;
 *   - a LONE punch after midday is a clock-out with the clock-in missing,
 *     not an arrival at 16:10 — read as an arrival, it made "late by nine
 *     hours" on hundreds of September days.
 *
 * Times are read in Botswana time whatever the viewer's computer says.
 * ------------------------------------------------------------------ */
import { SCHOOL } from '@/lib/school.config';
import { isHoliday as isStaticHoliday } from '@/lib/holidays';
import { isInTerm } from '@/lib/termDates';
import type { AttendanceDayStatus, AttendancePunch, StaffCategory } from '@/lib/hr';

export interface ClockingRules {
  /** "HH:MM", Monday–Friday. */
  weekdayStart: string;
  /** "HH:MM", or null when Saturday is not a working day. */
  saturdayStart: string | null;
  /** Public holidays have no start: nobody is late on one, and the few who
   *  come in (an exam on Botswana Day) are simply shown as worked. Screens
   *  pass the live list (usePublicHolidays); the default is the built-in one. */
  isHoliday?: (date: string) => boolean;
  /** School term time. Outside it (the school holidays) there is no start:
   *  staff keep holiday hours, so nobody is late and a day not come in is
   *  not an absence — LKC's rule from Oct 2026, after the first fortnight of
   *  September, before Term 3, showed nearly everyone over an hour late. */
  inTerm?: (date: string) => boolean;
}

/**
 * Start times per staff type (Oct 2026): a groundsman and a teacher do not
 * start at the same hour. Kept in hrClockingSettings/school and edited on
 * the Clocking screen. A type with nothing set uses the school's times.
 * An empty Saturday start means Saturday is not a working day for that
 * type, so nobody in it is late on one.
 */
export type CategoryStarts = Partial<Record<StaffCategory, { weekdayStart?: string; saturdayStart?: string | null }>>;

export const CLOCKING_SETTINGS_COLLECTION = 'hrClockingSettings';
export const CLOCKING_SETTINGS_DOC = 'school';

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The rules for one staff type: its own start times where set, the school's otherwise. */
export function rulesForCategory(base: ClockingRules, starts: CategoryStarts | undefined, category: StaffCategory | undefined): ClockingRules {
  const own = category ? starts?.[category] : undefined;
  if (!own) return base;
  return {
    ...base,
    weekdayStart: own.weekdayStart && HHMM.test(own.weekdayStart) ? own.weekdayStart : base.weekdayStart,
    saturdayStart: own.saturdayStart === undefined
      ? base.saturdayStart
      : own.saturdayStart && HHMM.test(own.saturdayStart) ? own.saturdayStart : null,
  };
}

export const SCHOOL_CLOCKING: ClockingRules = {
  ...SCHOOL.clocking,
  isHoliday: isStaticHoliday,
  inTerm: (d) => isInTerm(d),
};

const TZ = 'Africa/Gaborone';
const hhmmFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/** "HH:MM" of an instant, in Botswana time. */
export function localHHMM(iso: string): string {
  return hhmmFmt.format(new Date(iso));
}

function mins(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/** 0 = Sunday … 6 = Saturday, for a YYYY-MM-DD calendar date. */
export function weekday(date: string): number {
  return new Date(`${date}T12:00:00Z`).getUTCDay();
}

/** When the day starts, or null on a day with no start (Sunday). */
export function startFor(date: string, rules: ClockingRules = SCHOOL_CLOCKING): string | null {
  const d = weekday(date);
  if (d === 0) return null;
  if (rules.isHoliday?.(date)) return null;
  if (rules.inTerm && !rules.inTerm(date)) return null;
  if (d === 6) return rules.saturdayStart;
  return rules.weekdayStart;
}

/** A lone punch at or after this is a clock-out, not an arrival. */
const LONE_PUNCH_IS_OUT_FROM = '12:00';

export interface DayVerdict {
  /** First punch, "HH:MM" — null when the only punch was a clock-out. */
  firstIn: string | null;
  /** Last punch, when there is more than one. */
  lastOut: string | null;
  /** Minutes worked (sum of in→out pairs); null when a punch is missing. */
  minutesWorked: number | null;
  /** Odd number of punches: a clock-out (or clock-in) is missing. */
  missingPunch: boolean;
  /** The only punch was a clock-out; the clock-in is missing. */
  missingIn: boolean;
  /** Minutes after the start; 0 when on time. */
  lateMinutes: number;
  late: boolean;
  /** The status a screen should show. Records with no punches (absence,
   *  leave) keep the status they were given. */
  status: AttendanceDayStatus;
}

/**
 * The day, from its punches. `stored` is the record's own status, used only
 * when there are no punches to judge by.
 */
export function dayVerdict(
  rec: { date: string; punches: AttendancePunch[]; status?: AttendanceDayStatus },
  rules: ClockingRules = SCHOOL_CLOCKING,
): DayVerdict {
  const times = [...rec.punches].map((p) => localHHMM(p.at)).sort();
  if (times.length === 0) {
    const status = rec.status && rec.status !== 'half_day' && rec.status !== 'late' ? rec.status : 'absent';
    return { firstIn: null, lastOut: null, minutesWorked: null, missingPunch: false, missingIn: false, lateMinutes: 0, late: false, status };
  }
  if (times.length === 1 && times[0] >= LONE_PUNCH_IS_OUT_FROM) {
    return { firstIn: null, lastOut: times[0], minutesWorked: null, missingPunch: true, missingIn: true, lateMinutes: 0, late: false, status: 'present' };
  }
  const missingPunch = times.length % 2 === 1;
  let worked = 0;
  for (let i = 0; i + 1 < times.length; i += 2) worked += mins(times[i + 1]) - mins(times[i]);
  const start = startFor(rec.date, rules);
  const lateMinutes = start ? Math.max(0, mins(times[0]) - mins(start)) : 0;
  const late = lateMinutes > 0;
  return {
    firstIn: times[0],
    lastOut: times.length > 1 ? times[times.length - 1] : null,
    minutesWorked: missingPunch ? null : worked,
    missingPunch,
    missingIn: false,
    lateMinutes,
    late,
    status: late ? 'late' : 'present',
  };
}

/** "26 min", "1 h 05 min", "8 h". */
export function fmtMinutes(m: number): string {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${String(r).padStart(2, '0')} min` : `${h} h`;
}

/** Hours worked as "9 h 45 min", or a plain note when a punch is missing. */
export function fmtWorked(v: DayVerdict): string {
  if (v.minutesWorked === null) return v.missingIn ? 'No clock-in' : v.firstIn ? 'No clock-out' : '—';
  return fmtMinutes(v.minutesWorked);
}

export interface PeriodSummary {
  daysIn: number;
  lateDays: number;
  /** Total minutes late across the days that were late. */
  lateMinutes: number;
  /** Days where a punch is missing. */
  missingPunches: number;
  /** Minutes worked on the days where it is known. */
  minutesWorked: number;
  /** Average first punch, "HH:MM", over weekdays. */
  averageIn: string | null;
}

export function summarise(recs: { date: string; punches: AttendancePunch[]; status?: AttendanceDayStatus }[], rules: ClockingRules = SCHOOL_CLOCKING): PeriodSummary {
  let daysIn = 0, lateDays = 0, lateMinutes = 0, missingPunches = 0, minutesWorked = 0;
  const weekdayIns: number[] = [];
  for (const r of recs) {
    const v = dayVerdict(r, rules);
    if (!v.firstIn && !v.lastOut) continue;
    daysIn += 1;
    if (v.late) { lateDays += 1; lateMinutes += v.lateMinutes; }
    if (v.missingPunch) missingPunches += 1;
    if (v.minutesWorked !== null) minutesWorked += v.minutesWorked;
    const d = weekday(r.date);
    if (v.firstIn && d >= 1 && d <= 5) weekdayIns.push(mins(v.firstIn));
  }
  const avg = weekdayIns.length ? Math.round(weekdayIns.reduce((a, b) => a + b, 0) / weekdayIns.length) : null;
  return {
    daysIn, lateDays, lateMinutes, missingPunches, minutesWorked,
    averageIn: avg === null ? null : `${String(Math.floor(avg / 60)).padStart(2, '0')}:${String(avg % 60).padStart(2, '0')}`,
  };
}
